import { z } from 'zod';
import { mascararCEP } from './mascaras.js';
import { apenasDigitos } from './validacao.js';

/**
 * Endereço estruturado.
 *
 * Antes era um campo de texto livre, herdado do protótipo. Texto livre num endereço que
 * vai para instrumento contratual e para o cadastro do gateway significa erro de digitação
 * virando boleto sem entrega e contrato com endereço incompleto. Estruturado, o CEP puxa
 * quase tudo e sobra para a pessoa só o que o serviço não sabe: número e complemento.
 */
export const enderecoSchema = z.object({
  /** Só dígitos. Vazio é permitido enquanto a ficha está em cadastro. */
  cep: z
    .string()
    .trim()
    .max(9)
    .default('')
    .transform(apenasDigitos)
    .refine((v) => v === '' || v.length === 8, { message: 'CEP precisa ter 8 dígitos' }),
  logradouro: z.string().trim().max(160).default(''),
  numero: z.string().trim().max(20).default(''),
  complemento: z.string().trim().max(80).default(''),
  bairro: z.string().trim().max(80).default(''),
  cidade: z.string().trim().max(80).default(''),
  uf: z
    .string()
    .trim()
    .max(2)
    .default('')
    .transform((v) => v.toUpperCase())
    .refine((v) => v === '' || /^[A-Z]{2}$/.test(v), { message: 'UF precisa ter 2 letras' }),
});
export type Endereco = z.infer<typeof enderecoSchema>;

export const ENDERECO_VAZIO: Endereco = {
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
};

/**
 * Endereço em uma linha, como vai impresso no contrato:
 * "Av. Faria Lima, 3477, 12º andar · Itaim Bibi · São Paulo/SP · 04538-133"
 *
 * Partes ausentes somem junto com a pontuação — endereço incompleto não deve sair do
 * documento com vírgulas soltas anunciando o que faltou preencher.
 */
export function enderecoPorExtenso(endereco: Endereco): string {
  const rua = [endereco.logradouro, endereco.numero, endereco.complemento]
    .filter(Boolean)
    .join(', ');

  const municipio = [endereco.cidade, endereco.uf].filter(Boolean).join('/');

  return [rua, endereco.bairro, municipio, endereco.cep ? mascararCEP(endereco.cep) : '']
    .filter(Boolean)
    .join(' · ');
}

/** O que falta para o endereço servir a um documento contratual. */
export function pendenciasDoEndereco(endereco: Endereco): string[] {
  const faltando: string[] = [];
  if (!endereco.cep) faltando.push('CEP');
  if (!endereco.logradouro) faltando.push('logradouro');
  if (!endereco.numero) faltando.push('número');
  if (!endereco.cidade) faltando.push('cidade');
  if (!endereco.uf) faltando.push('UF');
  return faltando;
}

export function enderecoEstaCompleto(endereco: Endereco): boolean {
  return pendenciasDoEndereco(endereco).length === 0;
}

/** Resposta das consultas de CEP, já normalizada — o formato do provedor não vaza daqui. */
export const consultaDeCepSchema = z.object({
  cep: z.string(),
  logradouro: z.string(),
  bairro: z.string(),
  cidade: z.string(),
  uf: z.string(),
});
export type ConsultaDeCep = z.infer<typeof consultaDeCepSchema>;

/** Resposta da consulta de CNPJ, já normalizada. */
export const consultaDeCnpjSchema = z.object({
  cnpj: z.string(),
  razaoSocial: z.string(),
  nomeFantasia: z.string(),
  telefone: z.string(),
  email: z.string(),
  situacao: z.string(),
  endereco: enderecoSchema,
});
export type ConsultaDeCnpj = z.infer<typeof consultaDeCnpjSchema>;
